import { useForm } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toastFlash } from '@/lib/flash-toast';

export function CreateTrackDialog({ eventSlug }: { eventSlug: string }) {
  const [open, setOpen] = useState(false);
  const form = useForm({ name: '', description: '' });
  const { data, setData, processing, reset } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.post(`/admin/${eventSlug}/tracks`, {
      preserveScroll: true,
      onSuccess: (page) => {
        toastFlash(page, 'Track created');
        reset();
        setOpen(false);
      },
      onError: () =>
        toast({ type: 'error', description: 'Failed to create track' }),
    });
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        Add Track
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Track</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="create-track-name">Track Name</Label>
              <Input
                id="create-track-name"
                placeholder="e.g., AI & Machine Learning"
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
              />
              {errors.name && (
                <p className="text-destructive text-sm">{errors.name}</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-track-description">
                Description (optional)
              </Label>
              <Textarea
                id="create-track-description"
                placeholder="Brief description of this track"
                value={data.description}
                onChange={(e) => setData('description', e.target.value)}
              />
              {errors.description && (
                <p className="text-destructive text-sm">{errors.description}</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Creating...' : 'Create Track'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
